/**
 * @component CompanyHistoryPage
 * @description 창립부터 현재까지의 주요 이정표를 연도별로 나열하는 회사 연혁 페이지입니다.
 *              /company/history_edit 경로로 진입하면 관리자 모드에서 연혁을 추가/삭제할 수 있습니다.
 * @usage App 라우팅에서 `/company/history.html` 경로로 렌더링됩니다.
 */

import { useState, useEffect, useRef } from 'react';
import PageHero from '../../components/common/PageHero';
import SectionHeading from '../../components/common/SectionHeading';
import PasswordVerifyModal from '../../components/contact/PasswordVerifyModal';
import { pageMeta } from '../../constants/navigation';

// [기능] 연혁 배열을 연도별로 그룹화합니다. year 필드는 "YYYY.MM" 또는 "YYYY" 형식입니다.
function groupTimeline(items) {
  const map = new Map();
  for (const item of items) {
    const dotIdx = item.year.indexOf('.');
    const yearPart = dotIdx !== -1 ? item.year.slice(0, dotIdx) : item.year;
    const monthPart = dotIdx !== -1 ? item.year.slice(dotIdx + 1) : '';
    if (!map.has(yearPart)) map.set(yearPart, []);
    map.get(yearPart).push({ month: monthPart ? `${parseInt(monthPart, 10)}월` : '', text: item.text, rawYear: item.year });
  }
  return [...map.entries()];
}

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

function makeEmptyRow() {
  return { id: Date.now() + Math.random(), year: new Date().getFullYear(), month: 1, text: '' };
}

// [기술] 연도+월 숫자를 저장 형식 "YYYY.MM"으로 변환합니다.
function toYearString(year, month) {
  return `${year}.${String(month).padStart(2, '0')}`;
}

// [기술] 삭제 대상 식별을 위해 year+text 조합을 키로 사용합니다.
function makeDeleteKey(item) {
  return `${item.rawYear}||${item.text}`;
}

export default function CompanyHistoryPage({ onNavigate, adminTrigger }) {
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 관리자 인증 상태
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [adminKeyError, setAdminKeyError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [adminMode, setAdminMode] = useState(false);

  // 편집 상태
  const [deletedKeys, setDeletedKeys] = useState(new Set());
  const [pendingRows, setPendingRows] = useState([makeEmptyRow()]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const addSectionRef = useRef(null);

  // [비즈니스] /company/history_edit 경로 진입 시 관리자 인증 모달을 자동으로 엽니다.
  useEffect(() => {
    if (adminTrigger) setShowAdminModal(true);
  }, [adminTrigger]);

  // [기능] 백엔드 API에서 연혁 목록을 가져옵니다.
  useEffect(() => {
    fetch('/api/history')
      .then((r) => r.json())
      .then((data) => { setHistoryItems(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleVerifyAdmin = async () => {
    setIsVerifying(true);
    setAdminKeyError('');
    try {
      const res = await fetch('/api/history/verify', {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKeyInput },
      });
      if (res.ok) {
        setAdminKey(adminKeyInput);
        setAdminMode(true);
        setShowAdminModal(false);
        setAdminKeyInput('');
        // [비즈니스] 인증 성공 후 URL의 관리자 진입 경로를 정리합니다.
        if (adminTrigger) onNavigate('/company/history.html');
        setTimeout(() => addSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      } else {
        setAdminKeyError('관리자 키가 올바르지 않습니다.');
      }
    } catch {
      setAdminKeyError('서버 오류가 발생했습니다.');
    } finally {
      setIsVerifying(false);
    }
  };

  // [기능] 기존 항목을 삭제 대상으로 표시하거나 표시를 해제합니다.
  const toggleDelete = (key) => {
    setDeletedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // [기능] 마지막 pending row의 "+" 클릭 시 새 행을 추가합니다.
  const handleAddRow = () => {
    setPendingRows((prev) => [...prev, makeEmptyRow()]);
  };

  // [기능] pending row의 "-" 클릭 시 해당 행을 제거합니다.
  const handleRemovePendingRow = (id) => {
    setPendingRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length > 0 ? next : [makeEmptyRow()];
    });
  };

  const updatePendingRow = (id, field, value) => {
    setPendingRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };

  // [기능] 삭제/추가 내용을 합산해 전체 저장합니다. 백엔드에서 연도 내림차순 정렬됩니다.
  const handleSave = async () => {
    setSaveError('');
    setIsSaving(true);
    try {
      // 삭제 표시되지 않은 기존 항목 유지
      const remaining = historyItems.filter((item) => !deletedKeys.has(`${item.year}||${item.text}`));

      // 내용이 있는 pending rows만 추가
      const newEntries = pendingRows
        .filter((r) => String(r.text).trim() !== '')
        .map((r) => ({
          year: toYearString(r.year, r.month),
          text: String(r.text).trim(),
          highlight: false,
        }));

      const combined = [...remaining, ...newEntries];

      const res = await fetch('/api/history', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify(combined),
      });

      if (res.ok) {
        const saved = await res.json();
        setHistoryItems(saved);
        setDeletedKeys(new Set());
        setPendingRows([makeEmptyRow()]);
      } else {
        const msg = await res.text().catch(() => '');
        setSaveError(`저장 실패 (${res.status})${msg ? ': ' + msg : ''}`);
      }
    } catch {
      setSaveError('네트워크 오류가 발생했습니다. 서버가 실행 중인지 확인해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  const groups = groupTimeline(historyItems);
  const hasChanges = deletedKeys.size > 0 || pendingRows.some((r) => String(r.text).trim() !== '');

  return (
    <>
      <PageHero title={pageMeta.companyHistory.title} trail={pageMeta.companyHistory.trail} className="company-page-hero" />
      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="History"
            title="새힘정보기술이 걸어온 주요 이정표"
            description="산업 특화형 스마트 제조 솔루션 파트너로서 구축 경험과 운영 지원 역량을 확장해왔습니다."
            className="fade-in"
          />

          {adminMode && (
            <div className="history-admin-bar">
              <span className="history-admin-badge">관리자 모드</span>
              <span className="history-admin-hint">삭제할 항목의 [-] 버튼을 클릭하세요</span>
              <div className="history-admin-bar-actions">
                {saveError && <span className="history-admin-error">{saveError}</span>}
                <button
                  type="button"
                  className="history-admin-save-btn"
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                >
                  {isSaving ? '저장 중...' : '저장'}
                </button>
                <button
                  type="button"
                  className="history-admin-exit-btn"
                  onClick={() => { setAdminMode(false); setDeletedKeys(new Set()); setPendingRows([makeEmptyRow()]); setSaveError(''); }}
                >
                  모드 종료
                </button>
              </div>
            </div>
          )}

          <div className="history">
            {loading ? (
              <p style={{ color: 'rgba(32,35,50,0.5)', fontSize: '0.95rem' }}>로딩 중...</p>
            ) : (
              groups.map(([year, entries], idx) => (
                <div key={year} className={`history-group fade-in${idx === 0 ? ' is-first' : ''}`}>
                  <div className="history-year">{year}</div>
                  <div className="history-list">
                    <ul>
                      {entries.map((entry, i) => {
                        const deleteKey = makeDeleteKey(entry);
                        const isDeleted = deletedKeys.has(deleteKey);
                        return (
                          <li key={i} className={isDeleted ? 'history-entry-deleted' : ''}>
                            {entry.month && <span className="history-month">{entry.month}</span>}
                            <span className="history-text">{entry.text}</span>
                            {adminMode && (
                              <button
                                type="button"
                                className={`history-admin-row-btn ${isDeleted ? 'is-restore' : 'is-delete'}`}
                                onClick={() => toggleDelete(deleteKey)}
                                title={isDeleted ? '삭제 취소' : '삭제'}
                              >
                                {isDeleted ? '+' : '-'}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 관리자 모드: 새 연혁 추가 섹션 */}
          {adminMode && (
            <div className="history-admin-add-section" ref={addSectionRef}>
              <p className="history-admin-add-title">새 연혁 추가</p>
              <div className="history-admin-add-list">
                {pendingRows.map((row, idx) => {
                  const isLast = idx === pendingRows.length - 1;
                  return (
                    <div key={row.id} className="history-admin-add-row">
                      <input
                        type="number"
                        className="history-admin-input history-admin-input-year"
                        value={row.year}
                        min="1990"
                        max="2099"
                        onChange={(e) => updatePendingRow(row.id, 'year', e.target.value)}
                        placeholder="연도"
                      />
                      <select
                        className="history-admin-input history-admin-input-month"
                        value={row.month}
                        onChange={(e) => updatePendingRow(row.id, 'month', Number(e.target.value))}
                      >
                        {MONTHS.map((m) => (
                          <option key={m} value={m}>{m}월</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="history-admin-input history-admin-input-text"
                        value={row.text}
                        placeholder="내용을 입력하세요"
                        onChange={(e) => updatePendingRow(row.id, 'text', e.target.value)}
                      />
                      {isLast ? (
                        <button
                          type="button"
                          className="history-admin-row-btn is-add"
                          onClick={handleAddRow}
                          title="행 추가"
                        >
                          +
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="history-admin-row-btn is-delete"
                          onClick={() => handleRemovePendingRow(row.id)}
                          title="행 삭제"
                        >
                          -
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      <PasswordVerifyModal
        isOpen={showAdminModal}
        password={adminKeyInput}
        errorMessage={adminKeyError}
        isSubmitting={isVerifying}
        onChangePassword={(e) => setAdminKeyInput(e.target.value)}
        onConfirm={handleVerifyAdmin}
        onCancel={() => { setShowAdminModal(false); setAdminKeyInput(''); setAdminKeyError(''); }}
      />
    </>
  );
}
